import { useState, useEffect, useMemo } from "react";
import {
  Quote,
  MapPin,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { palette } from "../style_palete";
import { useReseñas } from "../hooks/useReseñas";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?q=kalmares&oq=kalmares&gs_lcrp=EgZjaHJvbWUqCggAEAAY4wIYgAQyCggAEAAY4wIYgAQyEAgBEC4YrwEYxwEYgAQYjgUyEQgCEC4YChgLGK8BGMcBGIAEMgsIAxAAGAoYCxiABDILCAQQABgKGAsYgAQyBggFEEUYPDIGCAYQRRg8MgYIBxBFGDzSAQgxNTcyajBqN6gCALACAA&sourceid=chrome&ie=UTF-8#lrd=0x95bccb1678f93a69:0xe621338a36db9420,1,,,,";

const GOOGLE_REVIEW_WRITE_URL =
  "https://www.google.com/search?q=kalmares&oq=kalmares&gs_lcrp=EgZjaHJvbWUqCggAEAAY4wIYgAQyCggAEAAY4wIYgAQyEAgBEC4YrwEYxwEYgAQYjgUyEQgCEC4YChgLGK8BGMcBGIAEMgsIAxAAGAoYCxiABDILCAQQABgKGAsYgAQyBggFEEUYPDIGCAYQRRg8MgYIBxBFGDzSAQgxNTcyajBqN6gCALACAA&sourceid=chrome&ie=UTF-8#lrd=0x95bccb1678f93a69:0xe621338a36db9420,3,,,,";

function cleanText(v) {
  if (!v) return "";
  let s = String(v).trim();
  if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1);
  return s.replace(/""/g, '"').trim();
}

export default function Testimonials() {
  const { reseñas, loading } = useReseñas(
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vS0xuBj1onfCZv9cdzsicTpCAiRBnBalp5Bb1Pz6hSDBGsa-lfSB90XsGV9zGrfIkSEUnw7VXUGLYBT/pub?gid=0&single=true&output=csv"
  );

  const normalized = useMemo(() => {
    if (!reseñas?.length) return [];
    return reseñas
      .map((r) => ({
        nombre: cleanText(r?.nombre),
        comentario: cleanText(r?.comentario),
      }))
      .filter((r) => r.nombre || r.comentario);
  }, [reseñas]);

  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < normalized.length; i += 3) {
      out.push(normalized.slice(i, i + 3));
    }
    return out;
  }, [normalized]);

  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!pages.length) return;
    const id = setInterval(
      () => setPage((p) => (p + 1) % pages.length),
      8000
    );
    return () => clearInterval(id);
  }, [pages.length]);

  if (loading) {
    return (
      <section className="py-20" style={{ backgroundColor: palette.sky }}>
        <div className="text-center text-slate-500 text-lg">
          Cargando testimonios reales…
        </div>
      </section>
    );
  }

  if (!pages.length) return null;

  return (
    <section className="py-20" style={{ backgroundColor: palette.sky }}>
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        {/* Título */}
        <div className="max-w-2xl">
          <div className="mb-6">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0F1B2D] mb-3">
              Nuestros pacientes
            </h2>
            <div className="w-36 h-1.5 bg-[#2E8BC0] rounded-full"></div>
          </div>

          <p className="text-[#5E738A] text-lg mt-5">
            Experiencias reales tomadas de nuestras reseñas públicas
          </p>
        </div>


        {/* Carrusel */}
        <div className="relative max-w-6xl mx-auto min-h-[320px] mt-12">
          {pages.map((group, idx) => (
              <div
                key={idx}
                className={`transition-opacity duration-700 ${
                  idx === page
                    ? "relative opacity-100"
                    : "absolute inset-0 opacity-0 pointer-events-none"
                }`}
              >

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {group.map((r, i) => (
                  <div
                    key={`${idx}-${i}`}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition 
                               p-7 min-h-[260px] flex flex-col justify-between"
                  >
                    <div>
                      <Quote className="h-8 w-8 text-[#2E8BC0] mb-4" />

                      <blockquote className="text-[#0F1B2D] text-lg leading-relaxed mb-6 line-clamp-5">
                        “{r.comentario}”
                      </blockquote>
                    </div>

                    <div>
                      <div className="mb-3">
                        <a
                          href={GOOGLE_REVIEWS_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-[#5E738A] hover:text-[#0F1B2D]"
                        >
                          <MapPin className="h-4 w-4" />
                          Ver en Google
                        </a>
                      </div>

                      <div className="border-t pt-3 text-sm font-semibold text-[#0F1B2D]">
                        {r.nombre}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Navegación */}
        <div className="mt-10 flex justify-center gap-6">
          <button
            onClick={() =>
              setPage((p) => (p - 1 + pages.length) % pages.length)
            }
            className="p-2 rounded-full bg-white shadow hover:shadow-md"
          >
            <ChevronLeft />
          </button>
          <button
            onClick={() => setPage((p) => (p + 1) % pages.length)}
            className="p-2 rounded-full bg-white shadow hover:shadow-md"
          >
            <ChevronRight />
          </button>
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <p className="text-[#1E3A5F] mb-5">
            ¿Ya nos visitaste? ¡Contanos tu experiencia!
          </p>

          <div className="flex justify-center gap-4 flex-wrap">
            <a
              href={GOOGLE_REVIEW_WRITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#5E738A] hover:bg-[#0F1B2D] 
                         text-white px-6 py-3 rounded-lg transition"
            >
              <MapPin className="h-5 w-5" />
              Dejá tu reseña en Google
            </a>

            <a
              href="https://www.instagram.com/kalmares.ba/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#2E8BC0] hover:bg-[#0F1B2D] 
                         text-white px-6 py-3 rounded-lg transition"
            >
              <MessageCircle className="h-5 w-5" />
              Seguinos en Instagram
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
